'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import WorldwayNav from '@/components/WorldwayNav';
import { ALL_JOURNEYS } from './journeyIndex';
import type { Journey } from './journeyIndex';

// ─── Filter Data ────────────────────────────────────────────────────────────

const REGIONS = [
{ label: 'Africa', sub: ['Botswana', 'Egypt', 'Ethiopia', 'Kenya', 'Morocco', 'Namibia', 'Rwanda', 'South Africa', 'Tanzania', 'Uganda', 'Zimbabwe'] },
{ label: 'Asia', sub: ['Bhutan', 'Cambodia', 'China', 'India', 'Indonesia', 'Japan', 'Jordan', 'Laos', 'Mongolia', 'Myanmar', 'Nepal', 'Sri Lanka', 'Thailand', 'Vietnam'] },
{ label: 'Europe', sub: ['Croatia', 'France', 'Greece', 'Iceland', 'Italy', 'Norway', 'Portugal', 'Scotland', 'Spain', 'Switzerland', 'Turkey'] },
{ label: 'Latin America', sub: ['Argentina', 'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Costa Rica', 'Ecuador', 'Galápagos', 'Mexico', 'Panama', 'Peru'] },
{ label: 'Middle East', sub: ['Israel', 'Oman', 'Saudi Arabia', 'UAE'] },
{ label: 'North America', sub: ['Alaska', 'Canada', 'USA'] },
{ label: 'Oceania', sub: ['Australia', 'Fiji', 'New Zealand', 'Papua New Guinea'] },
{ label: 'Polar', sub: ['Antarctic', 'Arctic', 'Greenland', 'Svalbard'] }];


const TRAVEL_STYLES = ['Small Group Journey', 'Private Journey', 'Tailor-Made', 'Family Journey', 'Solo Journey', 'Honeymoon & Romance', 'Expedition', 'Luxury Rail'];

const WAYS_TO_TRAVEL = [
'Classic Group Cruises', 'Expedition Cruises', 'Private Jet Journeys', 'Private Safaris', 'Private Yacht Journeys', 'River Cruises', 'Small Group Journeys', 'Train All Journeys'];


const INTERESTS = [
'A&K Sanctuary', 'Adventure', 'Art & Culture', 'Beaches & Islands', 'Culinary', 'Family', 'Festivals & Events', 'Gorillas', 'History & Heritage', 'Luxury', 'Nature & Wildlife', 'Photography', 'Polar', 'Safari', 'Spiritual', 'Trekking & Hiking', 'Wellness', 'Wine & Gastronomy'];


const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const YEARS = ['2025', '2026', '2027'];

const EXTRA_FILTERS = [
{ label: 'New', key: 'new' },
{ label: 'Offers', key: 'offers' }];


// ─── Journey Data (from journeyIndex) ───────────────────────────────────────
const JOURNEY_DATA: Journey[] = ALL_JOURNEYS;


// ─── Filter Section Component ────────────────────────────────────────────────

function FilterSection({ title, children, defaultOpen = true }: {title: string;children: React.ReactNode;defaultOpen?: boolean;}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[var(--border)] pb-4 mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full text-left mb-3">
        
        <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--foreground)]">{title}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`transition-transform duration-200 text-[var(--muted-foreground)] ${open ? 'rotate-180' : ''}`}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {open && <div>{children}</div>}
    </div>);

}

// ─── Journey Card Component ──────────────────────────────────────────────────

function JourneyCard({ journey }: {journey: Journey;}) {
  return (
    <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-xl overflow-hidden hover:border-[rgba(201,168,76,0.35)] transition-all duration-300 group flex flex-col">
      {/* Image */}
      <div className="relative h-44 overflow-hidden flex-shrink-0">
        <img
          src={journey.image}
          alt={journey.alt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {journey.badge &&
          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[var(--primary)] text-black">
              {journey.badge}
            </span>
          }
          {journey.isNew && !journey.badge &&
          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500 text-white">NEW</span>
          }
          {journey.hasOffer &&
          <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-500 text-white">OFFER</span>
          }
        </div>
        {/* Availability */}
        <div className="absolute bottom-2 right-2">
          <span className={`text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${journey.availability === 'Limited availability' ? 'bg-amber-500/90 text-black' : 'bg-emerald-500/90 text-white'}`}>
            {journey.availability}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-sm font-bold leading-snug mb-1.5 group-hover:text-[var(--primary)] transition-colors line-clamp-2" style={{ fontFamily: 'var(--font-serif)' }}>
          {journey.title}
        </h3>

        {/* Meta */}
        <div className="flex items-center gap-3 text-[10px] text-[var(--muted-foreground)] mb-2">
          <span className="flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
            {journey.days} days
          </span>
          <span className="flex items-center gap-1">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            {journey.groupSize}
          </span>
        </div>

        {/* Short description */}
        <p className="text-[10px] text-[var(--muted-foreground)] mb-3 line-clamp-2 leading-relaxed">
          {journey.shortDescription}
        </p>

        {/* Interests */}
        <div className="flex flex-wrap gap-1 mb-3">
          {journey.interests.slice(0, 2).map((interest) =>
          <span key={interest} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)] border border-[rgba(201,168,76,0.2)]">
              {interest}
            </span>
          )}
          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--surface-3)] text-[var(--muted-foreground)]">
            {journey.travelStyle}
          </span>
        </div>

        {/* Price & CTAs */}
        <div className="mt-auto pt-3 border-t border-[var(--border)]">
          <div className="text-xs font-bold text-[var(--primary)] mb-2">{journey.priceLabel}</div>
          <div className="flex gap-2">
            <Link
              href={`/journeys/${journey.slug}`}
              className="flex-1 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-lg bg-[var(--primary)] text-black hover:opacity-90 transition-opacity text-center">
              View Journey
            </Link>
            <Link
              href={`/tailor-made?ref=${encodeURIComponent(journey.title)}`}
              className="flex-1 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all text-center">
              Refine Journey
            </Link>
          </div>
        </div>
      </div>
    </div>);

}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function AllJourneysContent() {
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [expandedRegions, setExpandedRegions] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
  const [selectedWays, setSelectedWays] = useState<string[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [showOffers, setShowOffers] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [sortBy, setSortBy] = useState<'relevance' | 'price-asc' | 'price-desc' | 'duration'>('relevance');
  const [page, setPage] = useState(1);
  const PER_PAGE = 24;

  const toggleItem = (arr: string[], setArr: (v: string[]) => void, item: string) => {
    setArr(arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item]);
    setPage(1);
  };

  const toggleRegionExpand = (region: string) => {
    setExpandedRegions((prev) => prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]);
  };

  const clearAll = () => {
    setSelectedRegions([]);setSelectedCountries([]);setSelectedStyles([]);
    setSelectedWays([]);setSelectedInterests([]);setSelectedMonths([]);
    setSelectedYears([]);setShowNew(false);setShowOffers(false);setKeyword('');
    setPage(1);
  };

  const filtered = useMemo(() => {
    let result = JOURNEY_DATA.filter((j) => {
      if (selectedRegions.length && !selectedRegions.includes(j.region)) return false;
      if (selectedCountries.length && !selectedCountries.includes(j.country)) return false;
      if (selectedStyles.length && !selectedStyles.includes(j.travelStyle)) return false;
      if (selectedWays.length && !selectedWays.includes(j.wayToTravel)) return false;
      if (selectedInterests.length && !selectedInterests.some((i) => j.interests.includes(i))) return false;
      if (selectedMonths.length && !selectedMonths.some((m) => j.months.includes(m))) return false;
      if (selectedYears.length && !selectedYears.some((y) => j.years.includes(y))) return false;
      if (showNew && !j.isNew) return false;
      if (showOffers && !j.hasOffer) return false;
      if (keyword && !j.title.toLowerCase().includes(keyword.toLowerCase()) && !j.country.toLowerCase().includes(keyword.toLowerCase())) return false;
      return true;
    });

    if (sortBy === 'price-asc') result = [...result].sort((a, b) => a.price - b.price);else
    if (sortBy === 'price-desc') result = [...result].sort((a, b) => b.price - a.price);else
    if (sortBy === 'duration') result = [...result].sort((a, b) => b.days - a.days);

    return result;
  }, [selectedRegions, selectedCountries, selectedStyles, selectedWays, selectedInterests, selectedMonths, selectedYears, showNew, showOffers, keyword, sortBy]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const activeFilterCount = selectedRegions.length + selectedCountries.length + selectedStyles.length + selectedWays.length + selectedInterests.length + selectedMonths.length + selectedYears.length + (showNew ? 1 : 0) + (showOffers ? 1 : 0);

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />

      {/* Enterprise Preview Mode Banner */}
      <div className="bg-amber-500/8 border-b border-amber-500/20 py-2.5 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
            <span className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-semibold">
              Enterprise Preview Mode — Worldway × A&K
            </span>
            <span className="text-[10px] text-[var(--muted-foreground)] hidden sm:inline">
              · Official A&K catalogue integration pending. Content shown is for preview purposes only (noindex).
            </span>
          </div>
          <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 font-semibold uppercase tracking-widest flex-shrink-0">
            noindex · nofollow
          </span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative pt-20 pb-12 overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.2) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] mb-4">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <span>/</span>
            <Link href="/tours" className="hover:text-[var(--primary)] transition-colors">Journeys</Link>
            <span>/</span>
            <span className="text-[var(--foreground)]">All Journeys</span>
          </nav>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--primary)] mb-2">See Where We Can Take You</p>
              <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                All Journeys
              </h1>
              <p className="text-sm text-[var(--muted-foreground)] mt-2">
                {JOURNEY_DATA.length} extraordinary journeys across every continent
              </p>
            </div>
            {/* Search */}
            <div className="flex items-center gap-2 w-full md:w-80">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                <input
                  type="text"
                  placeholder="Search destinations..."
                  value={keyword}
                  onChange={(e) => {setKeyword(e.target.value);setPage(1);}}
                  className="w-full pl-9 pr-4 py-2 text-xs bg-[var(--surface-3)] border border-[var(--border)] rounded-lg text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--primary)]" />
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">

          {/* ── Left Filter Panel ── */}
          <aside className="hidden lg:block w-60 flex-shrink-0">
            <div className="sticky top-20">
              {/* Filter header */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--foreground)]">Filters</span>
                {activeFilterCount > 0 &&
                <button onClick={clearAll} className="text-[10px] text-[var(--primary)] hover:underline">
                    Clear all ({activeFilterCount})
                  </button>
                }
              </div>

              {/* REGIONS */}
              <FilterSection title="Regions">
                {REGIONS.map((region) =>
                <div key={region.label} className="mb-1">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                        type="checkbox"
                        checked={selectedRegions.includes(region.label)}
                        onChange={() => toggleItem(selectedRegions, setSelectedRegions, region.label)}
                        className="w-3 h-3 rounded border-[var(--border)] accent-[var(--primary)]" />
                      
                        <span className="text-[11px] text-[var(--muted-foreground)] group-hover:text-[var(--foreground)] transition-colors">{region.label}</span>
                      </label>
                      <button onClick={() => toggleRegionExpand(region.label)} className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] p-0.5">
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`transition-transform ${expandedRegions.includes(region.label) ? 'rotate-180' : ''}`}>
                          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                      </button>
                    </div>
                    {expandedRegions.includes(region.label) &&
                  <div className="ml-5 mt-1 space-y-0.5">
                        {region.sub.map((country) =>
                    <label key={country} className="flex items-center gap-2 cursor-pointer group">
                            <input
                        type="checkbox"
                        checked={selectedCountries.includes(country)}
                        onChange={() => toggleItem(selectedCountries, setSelectedCountries, country)}
                        className="w-3 h-3 rounded border-[var(--border)] accent-[var(--primary)]" />
                      
                            <span className="text-[10px] text-[var(--muted-foreground)] group-hover:text-[var(--foreground)] transition-colors">{country}</span>
                          </label>
                    )}
                      </div>
                  }
                  </div>
                )}
              </FilterSection>

              {/* TRAVEL STYLE */}
              <FilterSection title="Travel Style">
                {TRAVEL_STYLES.map((style) =>
                <label key={style} className="flex items-center gap-2 cursor-pointer group mb-1">
                    <input
                    type="checkbox"
                    checked={selectedStyles.includes(style)}
                    onChange={() => toggleItem(selectedStyles, setSelectedStyles, style)}
                    className="w-3 h-3 rounded border-[var(--border)] accent-[var(--primary)]" />
                  
                    <span className="text-[11px] text-[var(--muted-foreground)] group-hover:text-[var(--foreground)] transition-colors">{style}</span>
                  </label>
                )}
              </FilterSection>

              {/* WAYS TO TRAVEL */}
              <FilterSection title="Ways to Travel">
                {WAYS_TO_TRAVEL.map((way) =>
                <label key={way} className="flex items-center gap-2 cursor-pointer group mb-1">
                    <input
                    type="checkbox"
                    checked={selectedWays.includes(way)}
                    onChange={() => toggleItem(selectedWays, setSelectedWays, way)}
                    className="w-3 h-3 rounded border-[var(--border)] accent-[var(--primary)]" />
                  
                    <span className="text-[11px] text-[var(--muted-foreground)] group-hover:text-[var(--foreground)] transition-colors">{way}</span>
                  </label>
                )}
              </FilterSection>

              {/* INTERESTS */}
              <FilterSection title="Interests" defaultOpen={false}>
                <div className="flex flex-wrap gap-1.5">
                  {INTERESTS.map((interest) =>
                  <button
                    key={interest}
                    onClick={() => toggleItem(selectedInterests, setSelectedInterests, interest)}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-all ${selectedInterests.includes(interest) ? 'bg-[var(--primary)] text-black border-[var(--primary)]' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--foreground)]'}`}>
                    
                      {interest}
                    </button>
                  )}
                </div>
              </FilterSection>

              {/* MONTHS */}
              <FilterSection title="Months" defaultOpen={false}>
                <div className="flex flex-wrap gap-1.5">
                  {MONTHS.map((month) =>
                  <button
                    key={month}
                    onClick={() => toggleItem(selectedMonths, setSelectedMonths, month)}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-all ${selectedMonths.includes(month) ? 'bg-[var(--primary)] text-black border-[var(--primary)]' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--foreground)]'}`}>
                    
                      {month.slice(0, 3)}
                    </button>
                  )}
                </div>
              </FilterSection>

              {/* YEARS */}
              <FilterSection title="Years" defaultOpen={false}>
                <div className="flex gap-2">
                  {YEARS.map((year) =>
                  <button
                    key={year}
                    onClick={() => toggleItem(selectedYears, setSelectedYears, year)}
                    className={`text-[11px] px-3 py-1.5 rounded-lg border transition-all ${selectedYears.includes(year) ? 'bg-[var(--primary)] text-black border-[var(--primary)]' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--foreground)]'}`}>
                    
                      {year}
                    </button>
                  )}
                </div>
              </FilterSection>

              {/* EXTRA */}
              <FilterSection title="Edits" defaultOpen={false}>
                {EXTRA_FILTERS.map((f) =>
                <label key={f.key} className="flex items-center gap-2 cursor-pointer group mb-1">
                    <input
                    type="checkbox"
                    checked={f.key === 'new' ? showNew : showOffers}
                    onChange={() => {if (f.key === 'new') setShowNew(!showNew);else setShowOffers(!showOffers);setPage(1);}}
                    className="w-3 h-3 rounded border-[var(--border)] accent-[var(--primary)]" />
                  
                    <span className="text-[11px] text-[var(--muted-foreground)] group-hover:text-[var(--foreground)] transition-colors">{f.label}</span>
                  </label>
                )}
              </FilterSection>
            </div>
          </aside>

          {/* ── Journey Grid ── */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <p className="text-xs text-[var(--muted-foreground)]">
                Showing <span className="font-semibold text-[var(--foreground)]">{filtered.length}</span> journeys
                {activeFilterCount > 0 && <span className="text-[var(--primary)]"> (filtered)</span>}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-[var(--muted-foreground)] uppercase tracking-wider">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="text-xs bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-1.5 text-[var(--foreground)] focus:outline-none focus:border-[var(--primary)]">
                  
                  <option value="relevance">Relevance</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="duration">Duration</option>
                </select>
              </div>
            </div>

            {/* Mobile filter chips */}
            <div className="flex lg:hidden flex-wrap gap-2 mb-4">
              {REGIONS.map((r) =>
              <button
                key={r.label}
                onClick={() => toggleItem(selectedRegions, setSelectedRegions, r.label)}
                className={`text-[10px] px-2.5 py-1 rounded-full border transition-all ${selectedRegions.includes(r.label) ? 'bg-[var(--primary)] text-black border-[var(--primary)]' : 'border-[var(--border)] text-[var(--muted-foreground)]'}`}>
                
                  {r.label}
                </button>
              )}
            </div>

            {/* Grid */}
            {paginated.length === 0 ?
            <div className="text-center py-24">
                <div className="text-4xl mb-4">🌍</div>
                <p className="text-[var(--muted-foreground)] text-sm">No journeys match your filters.</p>
                <button onClick={clearAll} className="mt-4 text-xs text-[var(--primary)] hover:underline">Clear all filters</button>
              </div> :

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {paginated.map((journey) =>
              <JourneyCard key={journey.id} journey={journey} />
              )}
              </div>
            }

            {/* Pagination */}
            {totalPages > 1 &&
            <div className="flex items-center justify-center gap-2 mt-10">
                <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-xs border border-[var(--border)] rounded-lg text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                
                  ← Prev
                </button>
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                const pageNum = page <= 4 ? i + 1 : page + i - 3;
                if (pageNum < 1 || pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-8 h-8 text-xs rounded-lg border transition-all ${page === pageNum ? 'bg-[var(--primary)] text-black border-[var(--primary)]' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)]'}`}>
                    
                      {pageNum}
                    </button>);

              })}
                <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-xs border border-[var(--border)] rounded-lg text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                
                  Next →
                </button>
              </div>
            }
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-[var(--border)] py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 WorldwayLuxe · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
            <Link href="/small-group" className="hover:text-[var(--primary)] transition-colors">Small Group</Link>
            <Link href="/ai-concierge" className="hover:text-[var(--primary)] transition-colors">AI Concierge</Link>
          </div>
        </div>
      </footer>
    </div>);

}